import type { ReactNode } from "react";
import { useState, useEffect } from "react";
import { navigate } from "@reach/router";
import { useTranslation } from "react-i18next";
import { useFirebaseGame } from "src/hooks/useFirebaseGame";
import { useAuth } from "src/contexts/AuthContext";
import type { MultiplayerGame } from "src/core/types";
import type { Theme } from "src/theme";

type Props = {
  path: string;
};

const containerStyle = ({ colors }: Theme) => ({
  position: "fixed" as const,
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  display: "flex",
  flexDirection: "column" as const,
  background: colors.grey95,
  overflow: "hidden",
  zIndex: 1000
});

const headerStyle = ({ gridUnit }: Theme) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: gridUnit * 5,
  borderBottom: "2px solid #e0e0e0",
  background: "white"
});

const headerTextStyle = ({ gridUnit }: Theme) => ({ fontSize: gridUnit * 6, fontWeight: "bold", margin: 0 });
const MultiplayerLobby = (props: Props): ReactNode => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [activeGames, setActiveGames] = useState<MultiplayerGame[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const { getPlayerGames } = useFirebaseGame(null);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const fetchGames = async () => {
      setLoading(true);
      try {
        const games = await getPlayerGames(user.uid);
        if (!cancelled) {
          setActiveGames(games);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchGames();
    return () => {
      cancelled = true;
    };
  }, [user, getPlayerGames]);

  const getGameStatusText = (game: MultiplayerGame) => {
    if (game.status === "waiting") return t("multiplayerLobby.status.waiting");
    if (game.status === "active") return t("multiplayerLobby.status.active");
    if (game.status === "paused") return t("multiplayerLobby.status.paused");
    return t("multiplayerLobby.status.finished");
  };

  // Wait for authentication
  if (!user) {
    return (
      <div css={containerStyle}>
        <div
          css={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "white"
          }}
        >
          <div css={{ color: "#333" }}>{t("multiplayerLobby.authenticating")}</div>
        </div>
      </div>
    );
  }

  return (
    <div css={containerStyle}>
      <header css={headerStyle}>
        <h1 css={headerTextStyle}>{t("multiplayerLobby.title")}</h1>
        <button css={(theme) => ({ ...theme.primaryButton })} onClick={() => navigate("/")}>
          {t("multiplayerLobby.backToHome")}
        </button>
      </header>
      <div
        css={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "48px",
          padding: 40,
          background: "white",
          color: "#333",
          overflowY: "auto"
        }}
      >
        <div css={{ display: "flex", gap: 12, marginBottom: 30 }}>
          <button
            css={(theme) => ({
              ...theme.primaryButton,
              flex: 1
            })}
            onClick={() => navigate("/multiplayer/create")}
          >
            {t("multiplayerLobby.createGame")}
          </button>
          <button
            css={(theme) => ({
              ...theme.button,
              flex: 1
            })}
            onClick={() => navigate("/multiplayer/join")}
          >
            {t("multiplayerLobby.joinGame")}
          </button>
        </div>

        {loading ? (
          <div css={{ textAlign: "center", padding: 20 }}>{t("multiplayerLobby.loading")}</div>
        ) : activeGames.length > 0 ? (
          <div>
            <h3 css={{ marginBottom: 12 }}>{t("multiplayerLobby.yourActiveGames")}</h3>
            <div css={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {activeGames.map((game) => (
                <div
                  key={game.gameId}
                  css={{
                    padding: 12,
                    background: "#f5f5f5",
                    borderRadius: 6,
                    cursor: "pointer",
                    transition: "background 0.2s",
                    "&:hover": {
                      background: "#e0e0e0"
                    }
                  }}
                  onClick={() => navigate(`/multiplayer/game/${game.gameId}`)}
                >
                  <div css={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div css={{ fontWeight: "bold", marginBottom: 4 }}>
                        {t("multiplayerLobby.room", { code: game.roomCode })}
                      </div>
                      <div css={{ fontSize: 12, color: "#666" }}>{getGameStatusText(game)}</div>
                    </div>
                    <button
                      css={(theme) => ({
                        ...theme.button,
                        padding: "6px 12px",
                        fontSize: 12
                      })}
                    >
                      {t("multiplayerLobby.resume")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div css={{ textAlign: "center", padding: 20, color: "#666" }}>{t("multiplayerLobby.noActiveGames")}</div>
        )}
      </div>
    </div>
  );
};

export default MultiplayerLobby;
